import * as Styled from './invite-page.styled';

import { UserAddOutlined, UserSwitchOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import { FC } from 'react';
import { InviteErrorAction } from 'shared/src/types';

import type { CommonAuthProviderContextValue } from '../../providers/CommonAuthProvider/CommonAuthProvider';

const ALT_PRIMARY_BLUE = '#00cccc';

export interface InvitePageError {
  action: InviteErrorAction;
}

interface InvitePageErrorViewProps {
  error: InvitePageError;
  auth: CommonAuthProviderContextValue;
}

interface ActionInfo {
  isRecoverable: boolean;
  Icon?: typeof UserSwitchOutlined;
  title: string;
  subtitle?: string;
  actionText: string;
  action(): void;
}

export function determineInvitePageErrorDetails(
  error: InvitePageError,
  auth: CommonAuthProviderContextValue,
): ActionInfo {
  const { user, loginWithRedirect, logout } = auth;
  const { action } = error;

  switch (action.type) {
    case 'change-identity':
      return {
        isRecoverable: true,
        Icon: UserSwitchOutlined,
        title: 'You’re logged in with SSO, but this Organization requires an email address and password',
        subtitle: 'Switch accounts to sign in with different credentials.',
        actionText: 'Switch Accounts',
        action: () => loginWithRedirect({
          returnTo: window.location.toString(),
          connection: action.connectionHint,
          ...(user?.email ? { loginHint: user?.email } : {}),
        }),
      };
    case 'signup':
      return {
        isRecoverable: true,
        Icon: UserAddOutlined,
        title: 'You’re logged in with SSO, but this Organization requires an email address and password',
        subtitle: 'Sign up to enter credentials for a new account.',
        actionText: 'Sign Up',
        action: () => loginWithRedirect({
          returnTo: window.location.toString(),
          connection: action.connectionHint,
          screen_hint: 'signup',
          ...(user?.email ? { loginHint: user?.email } : {}),
        }),
      };
    case 'change-user':
      return {
        Icon: UserSwitchOutlined,
        isRecoverable: true,
        title: `Please log in with ${action.loginHint} to accept this invitation`,
        actionText: 'Switch Account',
        action: () => loginWithRedirect({
          returnTo: window.location.toString(),
          loginHint: action.loginHint,
          prompt: 'login',
        }),
      };
    case 'unresolvable':
      return {
        isRecoverable: false,
        title: action.message,
        actionText: 'Log Out',
        action: () => logout({
          returnTo: window.location.toString(),
        }),
      };
    case 'unknown':
    default:
      return {
        isRecoverable: false,
        title: 'Something went wrong',
        subtitle: 'There is something wrong with this link. Please contact support@cribl.io or join our community channel for help.',
        actionText: 'Log Out',
        action: () => logout({
          returnTo: window.location.toString(),
        }),
      };
  }
}

export const InvitePageErrorView: FC<InvitePageErrorViewProps> = ({ error, auth }) => {
  const errorDetails = determineInvitePageErrorDetails(error, auth);
  const colorOverride = errorDetails.isRecoverable ? ALT_PRIMARY_BLUE : undefined;

  return (
    <Styled.InviteResult
      status={!errorDetails.isRecoverable ? 'error' : 'info'}
      title={errorDetails.title}
      subTitle={errorDetails.subtitle}
      icon={errorDetails.Icon ? <errorDetails.Icon style={{ color: colorOverride }} /> : undefined}
      extra={[
        <Button
          key='error-action'
          type='primary'
          style={{ backgroundColor: colorOverride, borderColor: colorOverride }}
          danger={!errorDetails.isRecoverable ? true : false}
          onClick={errorDetails.action}
        >
          {errorDetails.actionText}
        </Button>,
      ]}
    />
  );
};
